import { ChevronRight, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ProductionDeclarationMaterialCommentCard } from '@/features/production-declaration/components/statement/production-declaration-material-comment-card';
import type { CorrectionRequest } from '@/types/core/purchase-order/correction-request';

type ProductionDeclarationMaterialCorrectionRequestsListProps = {
  audience?: 'internal' | 'supplier';
  canUpdateCommentResolution?: boolean;
  comments: CorrectionRequest[];
  isUpdatingCommentResolution?: boolean;
  onCommentResolutionChange?: (comment: CorrectionRequest) => void;
  onDeleteClick?: (comment: CorrectionRequest) => void;
  onEditClick?: (comment: CorrectionRequest) => void;
};

export function ProductionDeclarationMaterialCorrectionRequestsList({
  audience = 'internal',
  canUpdateCommentResolution = false,
  comments,
  isUpdatingCommentResolution = false,
  onCommentResolutionChange,
  onDeleteClick,
  onEditClick,
}: ProductionDeclarationMaterialCorrectionRequestsListProps) {
  const { i18n, t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const [areResolvedCommentsOpen, setAreResolvedCommentsOpen] = useState(false);
  const locale = i18n.language;
  const unresolvedComments = comments.filter((comment) => !comment.isResolved);
  const resolvedComments = comments.filter((comment) => comment.isResolved);
  const hasResolvedComments = resolvedComments.length > 0;
  const isSupplierAudience = audience === 'supplier';

  if (
    comments.length === 0 ||
    (isSupplierAudience && unresolvedComments.length === 0)
  ) {
    return null;
  }

  if (isSupplierAudience) {
    return (
      <div className='space-y-3'>
        {unresolvedComments.map((comment) => (
          <ProductionDeclarationMaterialCommentCard
            comment={comment}
            isUpdatingCommentResolution={isUpdatingCommentResolution}
            key={comment.id}
            locale={locale}
            variant='supplier'
          />
        ))}
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      {hasResolvedComments ? (
        <Collapsible
          className='space-y-3'
          onOpenChange={setAreResolvedCommentsOpen}
          open={areResolvedCommentsOpen}
        >
          {areResolvedCommentsOpen ? null : (
            <CollapsibleTrigger className='flex w-fit items-center gap-2 text-left text-declaration-muted-foreground text-sm transition-colors hover:text-declaration-foreground'>
              <ChevronRight aria-hidden className='size-5' />
              {t('materialCommodityOrigins.comments.viewResolved')}
            </CollapsibleTrigger>
          )}
          <CollapsibleContent className='space-y-3'>
            <CollapsibleTrigger className='flex w-fit items-center gap-2 text-left text-declaration-muted-foreground text-sm transition-colors hover:text-declaration-foreground'>
              <ChevronUp aria-hidden className='size-5' />
              {t('materialCommodityOrigins.comments.hideResolved')}
            </CollapsibleTrigger>
            {resolvedComments.map((comment) => (
              <ProductionDeclarationMaterialCommentCard
                comment={comment}
                isUpdatingCommentResolution={isUpdatingCommentResolution}
                key={comment.id}
                locale={locale}
                onCommentResolutionChange={
                  canUpdateCommentResolution
                    ? onCommentResolutionChange
                    : undefined
                }
                variant='resolved'
              />
            ))}
          </CollapsibleContent>
        </Collapsible>
      ) : null}
      {unresolvedComments.map((comment) => (
        <ProductionDeclarationMaterialCommentCard
          comment={comment}
          isUpdatingCommentResolution={isUpdatingCommentResolution}
          key={comment.id}
          locale={locale}
          onCommentResolutionChange={
            canUpdateCommentResolution ? onCommentResolutionChange : undefined
          }
          onDeleteClick={onDeleteClick}
          onEditClick={onEditClick}
          variant='unresolved'
        />
      ))}
    </div>
  );
}
